import { useEffect, useState } from "react";

import { getExerciseStats, setExerciseMax, type Exercise } from "../lib/api";
import { PERCENTS, RELIABLE_REPS, REP_TARGETS, epley1rm, weightForReps } from "../lib/oneRm";
import { haptic, notify } from "../lib/telegram";
import { BAR_KG, barFor, mround, plateLine, platesFor } from "../lib/weights";
import { BusyLabel } from "./DotLoader";
import Sheet from "./Sheet";

type Mode = "from-set" | "from-max" | "plates";

const MODES: { key: Mode; label: string }[] = [
  { key: "from-set", label: "1ПМ по подходу" },
  { key: "from-max", label: "Вес от 1ПМ" },
  { key: "plates", label: "Блины" },
];

const BARS = [
  { kg: 20, label: "Штанга 20" },
  { kg: 15, label: "Смит 15" },
  { kg: 10, label: "EZ 10" },
];

const num = (text: string): number | null => {
  const value = parseFloat(text.replace(",", "."));
  return Number.isFinite(value) && value > 0 ? value : null;
};

/** 101.33 → «101.5»: 1ПМ округляем до полкило, рабочие веса — до блина. */
const kg = (value: number): string => String(Number(value.toFixed(2)));

interface Props {
  /** Упражнения тренировки: для них подставляется и сохраняется 1ПМ. */
  exercises: Exercise[];
  onClose: () => void;
}

/**
 * Калькулятор в шапке тренировки (14.09.2026).
 *
 * Раньше это был только раскладчик блинов — дублёр того, что numpad и так
 * показывает под весом, поэтому им не пользовались. Теперь три вопроса, которые
 * реально задают у стойки: «сколько мой 1ПМ по этому подходу», «какой вес взять
 * на 5 раз» и «что вешать». 1ПМ можно сохранить упражнению — от него считаются
 * рекомендации программ с процентами.
 */
export default function CalcSheet({ exercises, onClose }: Props) {
  const [mode, setMode] = useState<Mode>("from-set");
  const [exerciseId, setExerciseId] = useState<number | null>(exercises[0]?.id ?? null);
  const [weight, setWeight] = useState("");
  const [reps, setReps] = useState("");
  const [max, setMax] = useState("");
  const [known, setKnown] = useState<{ value: number; tested: boolean } | null>(null);
  const [plateKg, setPlateKg] = useState("");
  const [bar, setBar] = useState(BAR_KG);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const exercise = exercises.find((e) => e.id === exerciseId) ?? null;

  // Сменили упражнение — подставляем его 1ПМ: ручной, если задан, иначе оценку
  // по истории. И гриф — под его снаряд.
  useEffect(() => {
    if (!exercise) return;
    let live = true;
    setKnown(null);
    setMax("");
    setMessage(null);
    const barKg = barFor(exercise.equipment);
    if (barKg !== null) setBar(barKg);
    getExerciseStats(exercise.id)
      .then((stats) => {
        if (!live) return;
        const value = stats.one_rm_kg ?? stats.estimated_1rm_kg;
        if (value) {
          setKnown({ value, tested: stats.one_rm_kg !== null });
          setMax(kg(value));
        }
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [exercise?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveMax(value: number) {
    if (!exercise || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const rounded = mround(value, 0.5);
      await setExerciseMax(exercise.id, rounded);
      setKnown({ value: rounded, tested: true });
      notify("success");
      setMessage(`1ПМ ${kg(rounded)} кг сохранён — рекомендации по процентам пересчитаются.`);
    } catch (e) {
      notify("error");
      setMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const w = num(weight);
  const r = num(reps);
  const fromSet = w !== null && r !== null ? epley1rm(w, Math.round(r)) : null;
  const m = num(max);
  const plates = num(plateKg);
  const layout = plates !== null ? platesFor(plates, bar) : null;

  const field = (title: string, value: string, set: (v: string) => void, placeholder: string) => (
    <label className="calc__field">
      <span className="card__title">{title}</span>
      <input
        className="input"
        inputMode="decimal"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          setMessage(null);
          set(e.target.value);
        }}
      />
    </label>
  );

  const repsTable = (oneRm: number) => (
    <div className="calc__table">
      {REP_TARGETS.map((n) => (
        <div key={n} className="calc__cell">
          <span className="calc__cell-key">×{n}</span>
          <span className="calc__cell-val">{kg(mround(weightForReps(oneRm, n)))}</span>
        </div>
      ))}
    </div>
  );

  const saveButton = (value: number) =>
    exercise && (known === null || Math.abs(mround(value, 0.5) - known.value) >= 0.25) ? (
      <button className="wo-btn" disabled={busy} onClick={() => void saveMax(value)}>
        {busy ? <BusyLabel text="Сохраняю" /> : `Сохранить 1ПМ для «${exercise.name_ru}»`}
      </button>
    ) : null;

  return (
    <Sheet title="Калькулятор" onClose={onClose}>
      <div className="calc">
        <div className="seg" role="tablist">
          {MODES.map((item) => (
            <button
              key={item.key}
              role="tab"
              aria-pressed={mode === item.key}
              className="seg__btn"
              onClick={() => {
                haptic();
                setMode(item.key);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        {mode !== "plates" && exercises.length > 0 && (
          <div className="chips" aria-label="Упражнение">
            {exercises.map((e) => (
              <button
                key={e.id}
                aria-pressed={e.id === exerciseId}
                className={`chip ${e.id === exerciseId ? "chip--active" : ""}`}
                onClick={() => {
                  haptic();
                  setExerciseId(e.id);
                }}
              >
                {e.name_ru}
              </button>
            ))}
          </div>
        )}

        {mode === "from-set" && (
          <>
            <div className="calc__inputs">
              {field("Вес, кг", weight, setWeight, "100")}
              {field("Повторы", reps, setReps, "5")}
            </div>
            {fromSet !== null ? (
              <>
                <div className="calc__result">
                  <span className="calc__result-key">оценка 1ПМ</span>
                  <span className="calc__result-val">{kg(mround(fromSet, 0.5))} кг</span>
                </div>
                <p className="calc__note">
                  {r !== null && r > RELIABLE_REPS
                    ? `Больше ${RELIABLE_REPS} повторов формула завышает — точнее по подходу на 3–8.`
                    : "Формула Эпли — та же, что в статистике. Столько же потянешь на:"}
                </p>
                {repsTable(fromSet)}
                {saveButton(fromSet)}
              </>
            ) : (
              <p className="calc__note">Вес и повторы подхода — посчитаю 1ПМ и веса на другие повторы.</p>
            )}
          </>
        )}

        {mode === "from-max" && (
          <>
            {field("1ПМ, кг", max, setMax, "120")}
            {known && (
              <p className="calc__note">
                {known.tested ? "Ваш сохранённый" : "Оценка по истории"}: {kg(known.value)} кг
              </p>
            )}
            {m !== null ? (
              <>
                <p className="calc__note">Доля от 1ПМ:</p>
                <div className="calc__table">
                  {PERCENTS.map((p) => (
                    <div key={p} className="calc__cell">
                      <span className="calc__cell-key">{p}%</span>
                      <span className="calc__cell-val">{kg(mround((m * p) / 100))}</span>
                    </div>
                  ))}
                </div>
                <p className="calc__note">На количество повторов:</p>
                {repsTable(m)}
                {saveButton(m)}
              </>
            ) : (
              <p className="calc__note">Введите 1ПМ — покажу рабочие веса по процентам и повторам.</p>
            )}
          </>
        )}

        {mode === "plates" && (
          <>
            {field("Вес на штанге, кг", plateKg, setPlateKg, "80")}
            <div className="chips chips--wrap">
              {BARS.map((b) => (
                <button
                  key={b.kg}
                  aria-pressed={bar === b.kg}
                  className={`chip ${bar === b.kg ? "chip--active" : ""}`}
                  onClick={() => {
                    haptic();
                    setBar(b.kg);
                  }}
                >
                  {b.label}
                </button>
              ))}
            </div>
            {plates !== null &&
              (layout === null ? (
                <p className="calc__note">Легче грифа — вешать нечего.</p>
              ) : (
                <div className="calc__result">
                  <span className="calc__result-key">на сторону</span>
                  <span className="calc__plates">{plateLine(layout.perSide)}</span>
                </div>
              ))}
            {layout && !layout.exact && (
              <p className="calc__note">Нацело не собирается — выйдет {kg(layout.achieved)} кг.</p>
            )}
          </>
        )}

        {message && <p className="calc__note">{message}</p>}
      </div>
    </Sheet>
  );
}
